import { nevisignFetch } from "./client.js";

export type ReaderInboxTask = {
  id: string;
  title: string;
  status: string;
  source: string;
  type: string;
  contractId: string | null;
  caseId: string | null;
  caseNo?: string | null;
  legalRequestId?: string | null;
  dueAtJalali: string | null;
  dueAtGregorian: string | null;
  overdue?: boolean;
  sourceRef?: { processId?: string } | null;
};

export type ReaderInboxResult = {
  tasks: ReaderInboxTask[];
  source: "api" | "error";
  error?: string;
};

export type ReaderCaseListItem = {
  id: string;
  caseNo: string;
  subNo: string;
  currentStatusLabel: string;
  primaryBranchName?: string | null;
  currentAssigneeName?: string | null;
  parties?: Array<{ name: string; roleLabels?: string[] }>;
  nextHearingDateJalali?: string | null;
};

export type ReaderContractListItem = {
  id: string;
  title: string;
  status: string;
  contractType: string;
  subject: string;
  sourceFileName?: string | null;
  progressMessage?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ReaderContractsResult = {
  items: ReaderContractListItem[];
  total: number;
  source: "api" | "empty" | "error";
  error?: string;
};

export type ReaderCaseDetailResult = {
  case: Record<string, unknown> | null;
  source: "api" | "error";
  error?: string;
};

export type ReaderCasesResult = {
  items: ReaderCaseListItem[];
  total: number;
  source: "api" | "empty" | "error";
  error?: string;
};

type PaginatedTasks = {
  items: ReaderInboxTask[];
  total: number;
};

type PaginatedCases = {
  items: ReaderCaseListItem[];
  total: number;
};

type PaginatedContracts = {
  items: ReaderContractListItem[];
  total: number;
};

const ACTIVE_TASK_STATUSES = new Set(["open", "in_progress", "blocked"]);
const KARTABL_PRIORITY_SOURCES = new Set([
  "contract_approval",
  "contract_signature",
]);

function isDashboardActiveTask(status: string): boolean {
  if (ACTIVE_TASK_STATUSES.has(status)) return true;
  return status !== "done" && status !== "cancelled";
}

function kartablSort(a: ReaderInboxTask, b: ReaderInboxTask): number {
  const aPri = KARTABL_PRIORITY_SOURCES.has(a.source) ? 0 : 1;
  const bPri = KARTABL_PRIORITY_SOURCES.has(b.source) ? 0 : 1;
  if (aPri !== bPri) return aPri - bPri;
  if (Boolean(a.overdue) !== Boolean(b.overdue)) return a.overdue ? -1 : 1;
  const aIn = a.status === "in_progress" ? 0 : 1;
  const bIn = b.status === "in_progress" ? 0 : 1;
  if (aIn !== bIn) return aIn - bIn;
  const aDue = a.dueAtGregorian
    ? Date.parse(a.dueAtGregorian)
    : Number.POSITIVE_INFINITY;
  const bDue = b.dueAtGregorian
    ? Date.parse(b.dueAtGregorian)
    : Number.POSITIVE_INFINITY;
  return aDue - bDue;
}

async function fetchTasksPage(
  apiBase: string,
  params: Record<string, string | number | boolean | undefined>,
  signal?: AbortSignal,
): Promise<PaginatedTasks> {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    qs.set(key, String(value));
  }
  const res = await nevisignFetch(apiBase, `/tasks?${qs.toString()}`, {
    method: "GET",
    signal,
    cache: "no-store",
  });
  if (!res.ok) {
    const authError =
      res.status === 401
        ? "برای مشاهده کارتابل وارد شوید."
        : res.status === 403
          ? "دسترسی به کارتابل ندارید."
          : `HTTP ${res.status}`;
    throw new Error(authError);
  }
  const json = (await res.json()) as PaginatedTasks;
  return {
    items: Array.isArray(json.items) ? json.items : [],
    total: json.total ?? 0,
  };
}

/** Aggregate open tasks, approvals, and signatures — mirrors panel KartablSection. */
export async function fetchReaderInbox(
  apiBase: string,
  opts?: { signal?: AbortSignal; limit?: number },
): Promise<ReaderInboxResult> {
  if (!apiBase.trim()) {
    return { tasks: [], source: "error", error: "آدرس API تنظیم نشده است." };
  }

  const limit = opts?.limit ?? 10;
  const queries: Array<Record<string, string | number | boolean>> = [
    { page: 1, pageSize: 20, mine: true, status: "open" },
    { page: 1, pageSize: 20, mine: true, status: "in_progress" },
    { page: 1, pageSize: 10, mine: true, status: "blocked" },
    { page: 1, pageSize: 20, mine: true, source: "contract_approval" },
    { page: 1, pageSize: 20, mine: true, source: "contract_signature" },
  ];

  try {
    const pages = await Promise.all(
      queries.map((q) => fetchTasksPage(apiBase, q, opts?.signal)),
    );
    const byId = new Map<string, ReaderInboxTask>();
    for (const page of pages) {
      for (const task of page.items) {
        if (!isDashboardActiveTask(task.status)) continue;
        byId.set(task.id, task);
      }
    }
    const tasks = [...byId.values()].sort(kartablSort).slice(0, limit);
    return { tasks, source: "api" };
  } catch (err) {
    const message = err instanceof Error ? err.message : "خطای شبکه";
    return { tasks: [], source: "error", error: message };
  }
}

/** Recent cases from platform GET /api/v1/cases. */
export async function fetchNevisignCases(
  apiBase: string,
  opts?: { signal?: AbortSignal; pageSize?: number; search?: string },
): Promise<ReaderCasesResult> {
  if (!apiBase.trim()) {
    return { items: [], total: 0, source: "empty" };
  }

  try {
    const qs = new URLSearchParams();
    qs.set("page", "1");
    qs.set("pageSize", String(opts?.pageSize ?? 8));
    const search = opts?.search?.trim();
    if (search) qs.set("search", search);
    const res = await nevisignFetch(apiBase, `/cases?${qs.toString()}`, {
      method: "GET",
      signal: opts?.signal,
      cache: "no-store",
    });
    if (!res.ok) {
      const authError =
        res.status === 401
          ? "برای مشاهده پرونده‌ها وارد شوید."
          : res.status === 403
            ? "دسترسی به پرونده‌ها ندارید."
            : null;
      return {
        items: [],
        total: 0,
        source: "error",
        error: authError ?? `HTTP ${res.status}`,
      };
    }
    const json = (await res.json()) as PaginatedCases;
    const items = Array.isArray(json.items) ? json.items : [];
    return {
      items,
      total: json.total ?? items.length,
      source: items.length ? "api" : "empty",
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : "خطای شبکه";
    return { items: [], total: 0, source: "error", error: message };
  }
}

/** Recent contracts from platform GET /api/v1/contracts (same as panel /contracts). */
export async function fetchNevisignContracts(
  apiBase: string,
  opts?: { signal?: AbortSignal; pageSize?: number; search?: string },
): Promise<ReaderContractsResult> {
  if (!apiBase.trim()) {
    return { items: [], total: 0, source: "empty" };
  }

  try {
    const qs = new URLSearchParams();
    qs.set("page", "1");
    qs.set("pageSize", String(opts?.pageSize ?? 8));
    const search = opts?.search?.trim();
    if (search) qs.set("search", search);
    const res = await nevisignFetch(apiBase, `/contracts?${qs.toString()}`, {
      method: "GET",
      signal: opts?.signal,
      cache: "no-store",
    });
    if (!res.ok) {
      const authError =
        res.status === 401
          ? "برای مشاهده قراردادها وارد شوید."
          : res.status === 403
            ? "دسترسی به قراردادها ندارید."
            : null;
      return {
        items: [],
        total: 0,
        source: "error",
        error: authError ?? `HTTP ${res.status}`,
      };
    }
    const json = (await res.json()) as PaginatedContracts;
    const items = Array.isArray(json.items) ? json.items : [];
    return {
      items,
      total: json.total ?? items.length,
      source: items.length ? "api" : "empty",
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : "خطای شبکه";
    return { items: [], total: 0, source: "error", error: message };
  }
}

/** Case detail from platform GET /api/v1/cases/:id (same as panel useCase). */
export async function fetchNevisignCase(
  apiBase: string,
  caseId: string,
  opts?: { signal?: AbortSignal },
): Promise<ReaderCaseDetailResult> {
  const id = caseId.trim();
  if (!apiBase.trim() || !id) {
    return { case: null, source: "error", error: "شناسه پرونده نامعتبر است." };
  }

  try {
    const res = await nevisignFetch(apiBase, `/cases/${encodeURIComponent(id)}`, {
      method: "GET",
      signal: opts?.signal,
      cache: "no-store",
    });
    if (!res.ok) {
      const authError =
        res.status === 401
          ? "برای مشاهده پرونده وارد شوید."
          : res.status === 403
            ? "دسترسی به این پرونده ندارید."
            : res.status === 404
              ? "پرونده یافت نشد."
              : null;
      return {
        case: null,
        source: "error",
        error: authError ?? `HTTP ${res.status}`,
      };
    }
    const json = (await res.json()) as { case?: Record<string, unknown> };
    if (!json.case || typeof json.case !== "object") {
      return { case: null, source: "error", error: "پاسخ نامعتبر" };
    }
    return { case: json.case, source: "api" };
  } catch (err) {
    const message = err instanceof Error ? err.message : "خطای شبکه";
    return { case: null, source: "error", error: message };
  }
}

export type ReaderAdliranSyncResult = {
  ok: boolean;
  created?: number;
  updated?: number;
  unchanged?: number;
  skipped?: number;
  error?: string;
};

async function readAdliranSyncError(res: Response): Promise<string> {
  try {
    const json = (await res.json()) as { message?: string; error?: string };
    return json.message || json.error || `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}

/** Import new Adl Iran cases into the current workspace (POST /adliran-sync/fetch-new). */
export async function importAdliranCases(apiBase: string): Promise<ReaderAdliranSyncResult> {
  try {
    const res = await nevisignFetch(apiBase, "/adliran-sync/fetch-new", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    });
    if (!res.ok) {
      return { ok: false, error: await readAdliranSyncError(res) };
    }
    const json = (await res.json()) as {
      result?: { created?: number; updated?: number; unchanged?: number; skipped?: number };
    };
    return {
      ok: true,
      created: json.result?.created ?? 0,
      updated: json.result?.updated ?? 0,
      unchanged: json.result?.unchanged ?? 0,
      skipped: json.result?.skipped ?? 0,
    };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : "خطای شبکه" };
  }
}

/** Refresh one case from Adl Iran (POST /adliran-sync/cases/:id/update). */
export async function refreshAdliranCase(apiBase: string, caseId: string): Promise<ReaderAdliranSyncResult> {
  const id = caseId.trim();
  if (!id) return { ok: false, error: "شناسه پرونده نامعتبر است." };
  try {
    const res = await nevisignFetch(apiBase, `/adliran-sync/cases/${encodeURIComponent(id)}/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    });
    if (!res.ok) {
      return { ok: false, error: await readAdliranSyncError(res) };
    }
    const json = (await res.json()) as {
      result?: { created?: number; updated?: number; unchanged?: number; skipped?: number };
    };
    return {
      ok: true,
      created: json.result?.created ?? 0,
      updated: json.result?.updated ?? 0,
      unchanged: json.result?.unchanged ?? 0,
      skipped: json.result?.skipped ?? 0,
    };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : "خطای شبکه" };
  }
}
